/**
 * Dialog de configuração do node de Notificação (send_notification)
 * Permite configurar N destinatários (cada um com modo independente),
 * inbox de envio e texto da mensagem compartilhado
 * 
 * Modos de destinatário:
 * - fixed: número de telefone fixo
 * - variable: variável do fluxo
 * - system_user: membro/usuário do tenant
 * - crm_seller: vendedor CRM específico
 * - deal_seller: vendedor atribuído ao negócio do contexto
 *
 * RELEASE: 2.1.0 - 2026-07-23 - Fallback via template WhatsApp Oficial (quando janela 24h fechada)
 * RELEASE: 2.0.0 - 2026-03-12 - Suporte a múltiplos destinatários (recipients[])
 * RELEASE: 1.0.0 - 2026-03-12 - Implementação inicial
 */

import { useState, useEffect, useRef } from 'react';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FlowNode,
  SendNotificationConfig,
  NotificationRecipient,
  NotificationRecipientMode,
  normalizeNotificationRecipients,
} from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { Bell, Phone, Variable, User, Users, Briefcase, Info, Plus, Trash2, FileText } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { InputWithVariables } from '../InputWithVariables';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { TextareaWithVariables } from '../TextareaWithVariables';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('ConfigureNotificationDialog');

/** Opções de modo de destinatário para o select */
const RECIPIENT_MODE_OPTIONS: { value: NotificationRecipientMode; label: string; icon: typeof Phone; description: string }[] = [
  { value: 'fixed', label: 'Número fixo', icon: Phone, description: 'Digitar telefone manualmente' },
  { value: 'variable', label: 'Variável', icon: Variable, description: 'Variável do fluxo com telefone' },
  { value: 'system_user', label: 'Usuário do sistema', icon: User, description: 'Membro do tenant' },
  { value: 'crm_seller', label: 'Vendedor CRM', icon: Users, description: 'Vendedor específico do CRM' },
  { value: 'deal_seller', label: 'Vendedor do Negócio', icon: Briefcase, description: 'Vendedor atribuído ao negócio' },
];

/** Cria um destinatário vazio com modo padrão */
function createEmptyRecipient(): NotificationRecipient {
  return { mode: 'fixed' };
}

interface ConfigureNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: SendNotificationConfig) => void;
  tenantId: string;
}

/**
 * Dialog de configuração do node de notificação — múltiplos destinatários
 */
export function ConfigureNotificationDialog({
  open,
  onOpenChange,
  node,
  onSave,
  tenantId,
}: ConfigureNotificationDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const { currentTenant } = useAuth();
  const existingConfig = node?.data.config as SendNotificationConfig | undefined;

  // ========================================
  // ESTADOS DO FORMULÁRIO
  // ========================================

  // Lista de destinatários
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([createEmptyRecipient()]);

  // Inbox e mensagem (compartilhados entre todos os destinatários)
  const [inboxId, setInboxId] = useState(existingConfig?.inbox_id || '');
  const [inboxName, setInboxName] = useState(existingConfig?.inbox_name || '');
  const [messageText, setMessageText] = useState(existingConfig?.message_text || '');

  // v2.1.0 — Estado do fallback via template WhatsApp Oficial
  const [useTemplateFallback, setUseTemplateFallback] = useState<boolean>(!!existingConfig?.use_template_fallback);
  const [fallbackTemplateId, setFallbackTemplateId] = useState<string>(existingConfig?.fallback_template_id || '');
  const [fallbackBodyParams, setFallbackBodyParams] = useState<string[]>(existingConfig?.fallback_template_body_params || []);

  // ========================================
  // QUERIES
  // ========================================

  /** Query para buscar inboxes ativas do tenant */
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['inboxes-notification', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type, identifier')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  /** Hook de vendedores CRM */
  const { activeSellers } = useCrmSellers();
  const sellers = activeSellers || [];

  // v2.1.0 — Detecta se inbox selecionada é WhatsApp Oficial (canal com regra 24h)
  const selectedInbox = inboxes.find((i: any) => i.id === inboxId);
  const isWhatsAppOficial = selectedInbox?.channel_type === 'whatsapp_oficial';

  /**
   * v2.1.0 — Query de templates aprovados da inbox.
   * Só dispara quando fallback está ligado E o inbox é WhatsApp Oficial
   * (zero custo quando desligado). staleTime alto reduz refetches.
   */
  const { data: fallbackTemplates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ['whatsapp-templates-fallback', inboxId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, name, language, category, components')
        .eq('inbox_id', inboxId)
        .eq('status', 'APPROVED')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!inboxId && isWhatsAppOficial && useTemplateFallback && open,
    staleTime: 5 * 60 * 1000,
  });

  // Template atualmente selecionado no fallback
  const selectedFallbackTemplate = fallbackTemplates.find((t: any) => t.id === fallbackTemplateId);

  // Extrai variáveis {{1}}, {{2}} do BODY do template selecionado
  const fallbackBodyText: string = selectedFallbackTemplate
    ? (Array.isArray((selectedFallbackTemplate as any).components)
        ? String(((selectedFallbackTemplate as any).components as any[]).find((c: any) => c?.type === 'BODY')?.text || '')
        : '')
    : '';
  const fallbackBodyVarNumbers = (() => {
    const matches = fallbackBodyText.match(/\{\{(\d+)\}\}/g) || [];
    const unique = [...new Set(matches)] as string[];
    return unique
      .map((m) => parseInt(m.replace(/\D/g, ''), 10))
      .sort((a, b) => a - b);
  })();

  // ========================================
  // SINCRONIZAR ESTADOS AO ABRIR
  // ========================================

  useEffect(() => {
    if (node && open) {
      const cfg = node.data.config as SendNotificationConfig | undefined;
      if (cfg) {
        // Normaliza para formato v2 (retrocompatível)
        const normalized = normalizeNotificationRecipients(cfg);
        setRecipients(normalized.length > 0 ? normalized : [createEmptyRecipient()]);
        setInboxId(cfg.inbox_id || '');
        setInboxName(cfg.inbox_name || '');
        setMessageText(cfg.message_text || '');
        // v2.1.0 — restaura estado do fallback
        setUseTemplateFallback(!!cfg.use_template_fallback);
        setFallbackTemplateId(cfg.fallback_template_id || '');
        setFallbackBodyParams(cfg.fallback_template_body_params || []);
      } else {
        setRecipients([createEmptyRecipient()]);
        setInboxId('');
        setInboxName('');
        setMessageText('');
        setUseTemplateFallback(false);
        setFallbackTemplateId('');
        setFallbackBodyParams([]);
      }
    }
  }, [node, open]);

  // ========================================
  // HANDLERS DE DESTINATÁRIOS
  // ========================================

  /** Atualiza um campo de um destinatário específico */
  const updateRecipient = (index: number, updates: Partial<NotificationRecipient>) => {
    setRecipients(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  /** Adiciona novo destinatário à lista */
  const addRecipient = () => {
    setRecipients(prev => [...prev, createEmptyRecipient()]);
  };

  /** Remove destinatário (mínimo 1) */
  const removeRecipient = (index: number) => {
    if (recipients.length <= 1) {
      toast.error('É necessário pelo menos 1 destinatário');
      return;
    }
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  // ========================================
  // HANDLER DE SAVE
  // ========================================

  const handleSave = () => {
    if (!node) return;

    // Validação de inbox e mensagem
    if (!inboxId) {
      toast.error('Selecione uma inbox para envio');
      return;
    }
    if (!messageText.trim()) {
      toast.error('O texto da notificação é obrigatório');
      return;
    }

    // Validação de cada destinatário
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      const label = `Destinatário ${i + 1}`;

      if (r.mode === 'fixed' && !r.phone?.trim()) {
        toast.error(`${label}: informe o número de telefone`);
        return;
      }
      if (r.mode === 'variable' && !r.variable?.trim()) {
        toast.error(`${label}: selecione a variável com o telefone`);
        return;
      }
      if (r.mode === 'system_user' && !r.user_id) {
        toast.error(`${label}: selecione o usuário do sistema`);
        return;
      }
      if (r.mode === 'crm_seller' && !r.seller_id) {
        toast.error(`${label}: selecione o vendedor CRM`);
        return;
      }
    }

    // v2.1.0 — Validação do fallback (apenas se ligado e canal Oficial)
    if (isWhatsAppOficial && useTemplateFallback) {
      if (!fallbackTemplateId) {
        toast.error('Fallback: selecione um template aprovado');
        return;
      }
      // Garante que todos os parâmetros do BODY estão preenchidos
      for (let i = 0; i < fallbackBodyVarNumbers.length; i++) {
        if (!fallbackBodyParams[i] || !fallbackBodyParams[i].trim()) {
          toast.error(`Fallback: preencha o parâmetro {{${fallbackBodyVarNumbers[i]}}} do template`);
          return;
        }
      }
    }

    // Monta config no formato v2
    const config: SendNotificationConfig = {
      recipients,
      inbox_id: inboxId,
      inbox_name: inboxName,
      message_text: messageText,
      nickname: nicknameRef.current?.getNickname(),
      // v2.1.0 — fallback template (só persiste quando aplicável)
      use_template_fallback: isWhatsAppOficial ? useTemplateFallback : false,
      fallback_template_id: isWhatsAppOficial && useTemplateFallback ? fallbackTemplateId : undefined,
      fallback_template_name: isWhatsAppOficial && useTemplateFallback
        ? (selectedFallbackTemplate as any)?.name
        : undefined,
      fallback_template_language: isWhatsAppOficial && useTemplateFallback
        ? (selectedFallbackTemplate as any)?.language
        : undefined,
      fallback_template_body_params: isWhatsAppOficial && useTemplateFallback
        ? fallbackBodyParams.slice(0, fallbackBodyVarNumbers.length)
        : undefined,
    };

    logger.debug('[ConfigureNotificationDialog] Salvando config v2:', config);
    onSave(node.id, config);
    onOpenChange(false);
    toast.success('Notificação configurada com sucesso');
  };

  // ========================================
  // HANDLER DE SELEÇÃO DE INBOX
  // ========================================

  const handleInboxChange = (value: string) => {
    setInboxId(value);
    const inbox = inboxes.find((i: any) => i.id === value);
    setInboxName(inbox?.name || '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            Configurar Notificação
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* ==========================================
              SEÇÃO 1: Identificação do Node
          ========================================== */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node?.id}
            defaultNickname={existingConfig?.nickname}
          />

          <Separator />

          {/* ==========================================
              SEÇÃO 2: Lista de Destinatários
          ========================================== */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Destinatários *</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Adicione um ou mais destinatários. Todos receberão a mesma mensagem.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRecipient}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                Adicionar
              </Button>
            </div>

            {/* Renderiza cada destinatário */}
            {recipients.map((recipient, index) => (
              <RecipientItem
                key={index}
                index={index}
                recipient={recipient}
                total={recipients.length}
                onUpdate={(updates) => updateRecipient(index, updates)}
                onRemove={() => removeRecipient(index)}
                tenantId={tenantId}
                sellers={sellers}
              />
            ))}
          </div>

          <Separator />

          {/* ==========================================
              SEÇÃO 3: Inbox de Envio (obrigatória)
          ========================================== */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Canal de Envio (Inbox) *</Label>
            <p className="text-xs text-muted-foreground">
              Selecione qual inbox/canal será usado para enviar a notificação
            </p>
            <Select value={inboxId} onValueChange={handleInboxChange} disabled={loadingInboxes}>
              <SelectTrigger>
                <SelectValue placeholder={loadingInboxes ? 'Carregando...' : 'Selecione a inbox...'} />
              </SelectTrigger>
              <SelectContent>
                {inboxes.map((inbox: any) => (
                  <SelectItem key={inbox.id} value={inbox.id}>
                    <span className="flex items-center gap-2">
                      <span>{inbox.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({inbox.channel_type})
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* ==========================================
              SEÇÃO 4: Texto da Notificação
          ========================================== */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Mensagem da Notificação *</Label>
            <p className="text-xs text-muted-foreground">
              Texto enviado a todos os destinatários. Suporta variáveis e formatação WhatsApp.
            </p>
            <TextareaWithVariables
              tenantId={tenantId}
              value={messageText}
              onChange={setMessageText}
              placeholder="Ex: Novo lead recebido! Nome: {{contact_name}}, Telefone: {{contact_phone}}"
              maxLength={4096}
              rows={4}
            />
          </div>

          {/* ==========================================
              SEÇÃO 5 (v2.1.0): Fallback via Template WhatsApp Oficial
              Aparece apenas quando a inbox selecionada é whatsapp_oficial
          ========================================== */}
          {isWhatsAppOficial && (
            <div className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileText className="h-4 w-4 text-emerald-600" />
                    Fallback: Enviar Template quando janela 24h estiver fechada
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Se o contato não interagiu nas últimas 24h, o WhatsApp Oficial exige uma
                    template aprovada. Com esta opção ativa, o sistema envia a template abaixo
                    automaticamente. Se a janela estiver aberta, envia o texto normal (economia).
                  </p>
                </div>
                <Switch
                  checked={useTemplateFallback}
                  onCheckedChange={setUseTemplateFallback}
                />
              </div>

              {useTemplateFallback && (
                <div className="space-y-3 pt-2 border-t border-emerald-200">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Template aprovado *</Label>
                    <Select
                      value={fallbackTemplateId}
                      onValueChange={(v) => { setFallbackTemplateId(v); setFallbackBodyParams([]); }}
                      disabled={loadingTemplates}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={loadingTemplates ? 'Carregando templates...' : 'Selecione um template aprovado'} />
                      </SelectTrigger>
                      <SelectContent>
                        {fallbackTemplates.length === 0 && !loadingTemplates && (
                          <div className="px-2 py-3 text-xs text-muted-foreground">
                            Nenhum template APROVADO encontrado para esta inbox.
                          </div>
                        )}
                        {fallbackTemplates.map((t: any) => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="flex items-center gap-2">
                              <FileText className="h-3.5 w-3.5" />
                              {t.name}
                              <span className="text-xs text-muted-foreground">({t.language})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedFallbackTemplate && fallbackBodyText && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Prévia do BODY</Label>
                      <div className="text-xs bg-white border rounded p-2 whitespace-pre-wrap">
                        {fallbackBodyText}
                      </div>
                    </div>
                  )}

                  {fallbackBodyVarNumbers.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm">Parâmetros do BODY *</Label>
                      <p className="text-[11px] text-muted-foreground">
                        Você pode usar variáveis do fluxo (ex: {'{{contact_name}}'}).
                      </p>
                      {fallbackBodyVarNumbers.map((num, idx) => (
                        <div key={num} className="space-y-1">
                          <Label className="text-xs">Variável {'{{'}{num}{'}}'}</Label>
                          <InputWithVariables
                            tenantId={tenantId}
                            value={fallbackBodyParams[idx] || ''}
                            onChange={(val) => {
                              const next = [...fallbackBodyParams];
                              next[idx] = val;
                              setFallbackBodyParams(next);
                            }}
                            placeholder={`Valor para {{${num}}}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ==========================================
            FOOTER: Botões de ação
        ========================================== */}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Bell className="h-4 w-4 mr-2" />
            Salvar Notificação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// COMPONENTE: Item de Destinatário (formulário individual)
// ============================================================================

interface RecipientItemProps {
  index: number;
  recipient: NotificationRecipient;
  total: number;
  onUpdate: (updates: Partial<NotificationRecipient>) => void;
  onRemove: () => void;
  tenantId: string;
  sellers: any[];
}

/**
 * Formulário de um único destinatário dentro da lista
 * Contém seletor de modo e campos específicos por modo
 */
function RecipientItem({
  index,
  recipient,
  total,
  onUpdate,
  onRemove,
  tenantId,
  sellers,
}: RecipientItemProps) {
  return (
    <div className="rounded-lg border border-amber-300/50 dark:border-amber-700/50 bg-amber-50/30 dark:bg-amber-950/10 p-3 space-y-3">
      {/* Cabeçalho: número + modo + botão remover */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 min-w-[20px]">
          #{index + 1}
        </span>

        {/* Seletor de modo */}
        <Select
          value={recipient.mode}
          onValueChange={(v) => onUpdate({ mode: v as NotificationRecipientMode })}
        >
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RECIPIENT_MODE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <SelectItem key={opt.value} value={opt.value}>
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" />
                    <span>{opt.label}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Botão remover (só aparece se há mais de 1) */}
        {total > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Campos específicos por modo */}
      {/* Input de telefone fixo com DDI + máscara (mesmo padrão do cadastro de contato) */}
      {recipient.mode === 'fixed' && (
        <div className="space-y-1">
          <PhoneInput
            value={recipient.phone || ''}
            onChange={(value) => onUpdate({ phone: value })}
            placeholder="(11) 99999-9999"
            className="[&_input]:h-8 [&_input]:text-xs [&_button]:h-8"
          />
        </div>
      )}

      {recipient.mode === 'variable' && (
        <div className="space-y-1">
          <VariablePickerCombobox
            tenantId={tenantId}
            value={recipient.variable_id || recipient.variable || ''}
            onChange={(variable) => {
              if (variable) {
                onUpdate({ variable: variable.name, variable_id: variable.id || '' });
              } else {
                onUpdate({ variable: '', variable_id: '' });
              }
            }}
            placeholder="Selecione a variável com o telefone..."
          />
        </div>
      )}

      {recipient.mode === 'system_user' && (
        <div className="space-y-1">
          <SystemUserSelector
            value={recipient.user_id || null}
            onSelect={(userId, user) => {
              onUpdate({
                user_id: userId || undefined,
                user_name: user?.full_name || user?.email || '',
              });
            }}
            placeholder="Selecionar usuário..."
          />
          <p className="text-[10px] text-muted-foreground">
            O telefone será buscado do perfil do usuário
          </p>
        </div>
      )}

      {recipient.mode === 'crm_seller' && (
        <div className="space-y-1">
          <Select
            value={recipient.seller_id || ''}
            onValueChange={(v) => {
              const seller = sellers.find((s: any) => s.id === v);
              onUpdate({ seller_id: v, seller_name: seller?.name || '' });
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Selecione o vendedor..." />
            </SelectTrigger>
            <SelectContent>
              {sellers.map((seller: any) => (
                <SelectItem key={seller.id} value={seller.id}>
                  <span className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5" />
                    {seller.name}
                    {seller.phone && (
                      <span className="text-xs text-muted-foreground">({seller.phone})</span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {recipient.mode === 'deal_seller' && (
        <Alert variant="default" className="py-2">
          <Briefcase className="h-3.5 w-3.5" />
          <AlertDescription className="text-[10px]">
            Busca automática do vendedor atribuído ao negócio do contexto CRM.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
